import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import fundService from '../../services/fundService'

// Async thunks
export const fetchFundSummary = createAsyncThunk('fundManagement/fetchFundSummary', async (_, { rejectWithValue }) => {
    try {
        const response = await fundService.fetchFundsSummary(
            {
                showSnackbar: true,
                showSuccessSnackbar: false,
                successMessage: 'Wallet summary fetched successfully!',
                errorMessage: 'Failed to fetch wallet summary'
            }
        )
        return response
    } catch (error) {
        return rejectWithValue(error.message)
    }
})

export const fetchInvoices = createAsyncThunk('fundManagement/fetchInvoices', async ({ pageNumber = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
    try {
        const response = await fundService.getInvoices(
            pageNumber,
            pageSize,
            {
                showSnackbar: true,
                showSuccessSnackbar: false,
                successMessage: 'Invoices fetched successfully!',
                errorMessage: 'Failed to fetch invoices'
            }
        )
        return response
    } catch (error) {
        return rejectWithValue(error.message)
    }
})

export const purchaseFunds = createAsyncThunk('fundManagement/purchaseFunds', async (amount, { rejectWithValue }) => {
    try {

        const payload = {
            currencyamount: amount
        }

        const response = await fundService.purchaseFunds(
            payload,
            {
                showSnackbar: true,
                showSuccessSnackbar: true,
                successMessage: 'Your request to purchase funds will be sent to our team to process. Once received we will send you a confirmation email. Thank you.',
                errorMessage: 'Failed to purchase funds. Please try again.',
                autoHideDuration: 8000
            }
        )
        return response
    } catch (error) {
        return rejectWithValue(error.message)
    }
})

export const getFundsCSVTemplate = createAsyncThunk('fundManagement/getFundsCSVTemplate', async (_, { rejectWithValue }) => {
    try {
        const response = await fundService.fetchFundsTemplate(
            {
                showSnackbar: true,
                showSuccessSnackbar: true,
                successMessage: 'Funds CSV template fetched successfully!',
                errorMessage: 'Failed to fetch funds CSV template'
            }
        )
        return response
    } catch (error) {
        return rejectWithValue(error.message)
    }
})


// Initial state
const initialState = {
    wallet: {
        balance: 0,
        issuetotal: 0,
        purchasetotal: 0,
        currency: 'ZAR',
    },
    invoices: [],
    totalInvoices: 0,
    loading: {
        wallet: false,
        invoices: false,
        purchase: false,
        csvTemplate: false,
    },
    error: {
        wallet: null,
        invoices: null,
        purchase: null,
        csvTemplate: null,
    },
}

// Slice
const fundManagementSlice = createSlice({
    name: 'fundManagement',
    initialState,
    reducers: {
        clearErrors: (state) => {
            state.error = {
                wallet: null,
                invoices: null,
                purchase: null,
                csvTemplate: null,
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch wallet info
        builder
            .addCase(fetchFundSummary.pending, (state) => {
                state.loading.wallet = true
                state.error.wallet = null
            })
            .addCase(fetchFundSummary.fulfilled, (state, action) => {
                state.loading.wallet = false
                state.wallet = {
                    ...action.payload,
                    currency: action.payload.currency || 'ZAR', // Default to ZAR if not provided
                }
            })
            .addCase(fetchFundSummary.rejected, (state, action) => {
                state.loading.wallet = false
                state.error.wallet = action.payload
            })

        // Fetch invoices
        builder
            .addCase(fetchInvoices.pending, (state) => {
                state.loading.invoices = true
                state.error.invoices = null
            })
            .addCase(fetchInvoices.fulfilled, (state, action) => {
                state.loading.invoices = false
                state.invoices = action.payload.batches || []
                state.totalInvoices = action.payload.totalbatchcount || 0
            })
            .addCase(fetchInvoices.rejected, (state, action) => {
                state.loading.invoices = false
                state.error.invoices = action.payload
            })

        // Purchase funds
        builder
            .addCase(purchaseFunds.pending, (state) => {
                state.loading.purchase = true
                state.error.purchase = null
            })
            .addCase(purchaseFunds.fulfilled, (state, action) => {
                state.loading.purchase = false
            })
            .addCase(purchaseFunds.rejected, (state, action) => {
                state.loading.purchase = false
                state.error.purchase = action.payload
            })

        // Fetch funds CSV template
        builder
            .addCase(getFundsCSVTemplate.pending, (state) => {
                state.loading.csvTemplate = true
                state.error.csvTemplate = null
            })
            .addCase(getFundsCSVTemplate.fulfilled, (state, action) => {
                state.loading.csvTemplate = false
            })
            .addCase(getFundsCSVTemplate.rejected, (state, action) => {
                state.loading.csvTemplate = false
                state.error.csvTemplate = action.payload
            })
    },
})

export const { clearErrors } = fundManagementSlice.actions

export default fundManagementSlice.reducer
